/**
 * Wishlist functionality using localStorage.
 * Manages adding/removing products and updating UI state across all wishlist buttons.
 */

const STORAGE_KEY = 'tuna-wishlist';

const Wishlist = {
  /**
   * Get all wishlist product handles from localStorage.
   * @returns {string[]}
   */
  getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  },

  /**
   * Save items to localStorage and dispatch update event.
   * @param {string[]} items
   */
  _save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
  },

  /**
   * Check if a product handle is in the wishlist.
   * @param {string} handle
   * @returns {boolean}
   */
  has(handle) {
    return this.getItems().includes(handle);
  },

  /**
   * Add a product handle to the wishlist.
   * @param {string} handle
   */
  add(handle) {
    const items = this.getItems();
    if (!items.includes(handle)) {
      items.push(handle);
      this._save(items);
    }
  },

  /**
   * Remove a product handle from the wishlist.
   * @param {string} handle
   */
  remove(handle) {
    const items = this.getItems().filter((h) => h !== handle);
    this._save(items);
  },

  /**
   * Toggle a product handle in the wishlist.
   * @param {string} handle
   * @returns {boolean} - true if added, false if removed
   */
  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  /**
   * Get the count of items in the wishlist.
   * @returns {number}
   */
  count() {
    return this.getItems().length;
  },
};

/**
 * Update all wishlist button states on the page.
 */
function updateWishlistButtons() {
  document.querySelectorAll('[data-wishlist-button]').forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const isActive = Wishlist.has(handle);
    btn.classList.toggle('wishlist-button--active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
    syncButtonText(btn, isActive);
  });
}

/**
 * Sync the visible text and aria-label of a wishlist button.
 * Only applies to buttons with data-add-text / data-remove-text.
 */
function syncButtonText(btn, isActive) {
  const addText = btn.dataset.addText;
  const removeText = btn.dataset.removeText;
  if (!addText) return;
  const text = isActive ? removeText : addText;
  const label = btn.querySelector('.wishlist-button__text');
  if (label) label.textContent = text;
  btn.setAttribute('aria-label', text);
  btn.setAttribute('title', text);
}

/**
 * Update the header wishlist count bubble.
 * Both visibility and count text are driven by <html> state
 * (class + CSS custom property) so they survive section morphs.
 */
function updateWishlistCount() {
  const count = Wishlist.count();
  const root = document.documentElement;
  root.classList.toggle('has-wishlist-items', count > 0);
  root.style.setProperty('--wishlist-count-text', `"${count}"`);
}

/**
 * Handle click events on wishlist buttons via event delegation.
 */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-button]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();

  const handle = btn.dataset.productHandle;
  if (!handle) return;

  const added = Wishlist.toggle(handle);
  btn.classList.toggle('wishlist-button--active', added);
  btn.setAttribute('aria-pressed', String(added));
  syncButtonText(btn, added);
});

// Listen for wishlist changes (including from other tabs via storage event)
document.addEventListener('wishlist:change', () => {
  updateWishlistButtons();
  updateWishlistCount();
});

window.addEventListener('storage', (e) => {
  if (e.key === STORAGE_KEY) {
    updateWishlistButtons();
    updateWishlistCount();
  }
});

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    updateWishlistButtons();
    updateWishlistCount();
  });
} else {
  updateWishlistButtons();
  updateWishlistCount();
}

// Expose globally for the wishlist page to use
window.Wishlist = Wishlist;
